// src/admin/sections/JournalSection.jsx
//
// Ticket 59. Admin: Journal, shared entries only. The id stays `reflections`, so
// /admin/reflections still opens it; the table stays `reflections`. The screen,
// the sidebar and the rows say Journal and journal entries, the reader's words
// (ProfileBlocks.jsx: "the reading journal", "a journal entry"). A reflection is
// what a reader writes inside an entry, and this screen never shows that.
//
// WHAT AN ADMIN CHECKS HERE
//   A shared entry is a book and a prompt. The prompt's label is public (on the
//   card and the share image), and when a reader picks "type your own" the label
//   is whatever they typed. That label, and which book it is on, are what is
//   moderated. So a row is the label, the book and the reader's username, and
//   nothing the reader wrote inside the entry.
//
// WHERE THE RULE LIVES
//   Not in this page. Admins cannot read `reflections` at all (the only policy is
//   the owner's). The list comes from admin_shared_journal_entries(), which reads
//   the view readers already see, so it can only return a shared entry's public
//   columns. Hide goes through admin_hide_shared_entry() and Dismiss through
//   admin_dismiss_shared_entry(); neither touches anything else. This file calls
//   those two, plus the list, and reads no table directly.
//
// EVERY ACTION LOOKS AT THE ANSWER
//   An error with a code means the database answered and changed nothing. An error
//   with no code means no answer: the change may have gone through, so the screen
//   says it can't tell and reloads. A 0 means the entry isn't shared any more.
//   The audit line is written by the function, after the change and only when a
//   row changed, so it cannot say more than happened.
//
// There is no Warn reader: neither app has anything to deliver one (decided 22 Oct).
// Nothing checks an entry when it goes from private to shared (parked 8 Oct).

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

export const PAGE = 25;

const TONE = {
  ok:   { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

// ── pure helpers ─────────────────────────────────────────────────────────────

// 'type your own' is the one kind whose label is the reader's own words.
export const isTypedLabel = (row) => row?.prompt_kind === 'freeform';

export const entryCountText = (n) => `${Number(n).toLocaleString()} shared ${Number(n) === 1 ? 'entry' : 'entries'}`;

export const profileHref = (username) => (username ? `/u/${encodeURIComponent(username)}` : null);

// What an action's answer means. `error` is the Supabase error or null; `n` the function's number.
//   'done'     the database changed the entry
//   'gone'     it was not shared any more (already private, or deleted)
//   'refused'  the database answered with an error: nothing changed
//   'unknown'  no answer at all: it may or may not have changed
export function actionOutcome(error, n) {
  if (error) return error.code ? { kind: 'refused', message: error.message } : { kind: 'unknown', message: error.message || 'no answer' };
  return Number(n) === 1 ? { kind: 'done' } : { kind: 'gone' };
}

// ── pieces ───────────────────────────────────────────────────────────────────

function Btn({ children, onClick, disabled, danger, primary, label }) {
  const base = { fontFamily: 'inherit', fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '6px 12px', borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' };
  const look = primary
    ? { background: 'var(--neutral-800)', color: 'var(--brand-white)', border: '1px solid var(--neutral-800)' }
    : danger
      ? { background: '#fff8f8', color: '#991b1b', border: '1px solid #fca5a5' }
      : { background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' };
  return <button type="button" aria-label={label} disabled={disabled} onClick={onClick} style={{ ...base, ...look }}>{children}</button>;
}

function Note({ result }) {
  if (!result) return null;
  const t = TONE[result.tone] || TONE.warn;
  return (
    <div role={result.tone === 'ok' ? 'status' : 'alert'} data-row-note style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
      {result.text}
    </div>
  );
}

function EntryRow({ row, isMobile, busy, confirming, result, onAskHide, onCancelHide, onHide, onDismiss }) {
  const href = profileHref(row.username);
  const typed = isTypedLabel(row);
  return (
    <div data-entry={row.id} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', background: row.prompt_color || 'var(--neutral-300)', border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
            <span data-label style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', overflowWrap: 'anywhere' }}>{row.prompt_emoji} {row.prompt_label}</span>
            {typed && <span data-typed title="The reader picked &ldquo;type your own&rdquo;, so this label is their own words." style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-xxl)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}` }}>typed by the reader</span>}
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', marginBottom: 4 }}>
            on <strong data-book>{row.book_title || 'a book that is no longer in the catalog'}</strong>{row.book_author ? ` by ${row.book_author}` : ''}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
            shared by{' '}
            {href
              ? <a data-reader href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--neutral-700)', fontWeight: 700 }}>@{row.username}</a>
              : <span data-reader>a reader with no username</span>}
            {' '}· {new Date(row.created_at).toLocaleDateString()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn danger disabled={busy} onClick={() => onAskHide(row)}>Hide from community</Btn>
          <Btn disabled={busy} onClick={() => onDismiss(row)}>Dismiss</Btn>
        </div>
      </div>
      {confirming && (
        <div role="alertdialog" aria-label="Confirm hide" style={{ marginTop: 10, padding: '10px 12px', background: TONE.warn.bg, border: `1px solid ${TONE.warn.border}`, borderRadius: 'var(--radius-md)' }}>
          <div style={{ fontSize: 'var(--font-size-sm)', color: TONE.warn.color, fontWeight: 600, marginBottom: 8 }}>
            Hide this entry from the community? It goes back to private: it leaves {row.username ? `@${row.username}'s` : 'the reader\'s'} public profile and keeps feeding matching. The reader can share it again.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn danger disabled={busy} onClick={() => onHide(row)}>{busy ? 'Hiding…' : 'Yes, hide it'}</Btn>
            <Btn disabled={busy} onClick={onCancelHide}>Keep it shared</Btn>
          </div>
        </div>
      )}
      <Note result={result} />
    </div>
  );
}

// ── the screen ───────────────────────────────────────────────────────────────

export default function JournalSection({ isMobile }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);       // null: not read. Never 0 by default.
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [results, setResults] = useState({});
  const [banner, setBanner] = useState(null);

  const setResult = (id, r) => setResults(prev => ({ ...prev, [id]: r }));

  const load = useCallback(async (p = page) => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_shared_journal_entries', { p_limit: PAGE, p_offset: p * PAGE });
    if (error) {
      console.error('[Journal] list failed:', error.message);
      setRows([]); setTotal(null); setLoadError(error.message); setLoading(false);
      return;
    }
    const list = Array.isArray(data) ? data : [];
    // Hiding or dismissing the last row of a page leaves it empty while earlier pages are not: step back.
    if (!list.length && p > 0) { setPage(p - 1); return; }
    setLoadError(null);
    setRows(list);
    setTotal(list.length ? Number(list[0].total) : 0);
    setLoading(false);
  }, [page]);

  useEffect(() => { load(page); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  async function act(row, fn, verb, past) {
    setBusyId(row.id);
    setResult(row.id, null);
    setBanner(null);
    const { data, error } = await supabase.rpc(fn, { p_id: row.id });
    const out = actionOutcome(error, data);
    setBusyId(null);
    setConfirmId(null);
    if (out.kind === 'done') {
      setBanner({ tone: 'ok', text: `${past} "${row.prompt_label}"${row.username ? ` by @${row.username}` : ''}.` });
      await load(page);
      return;
    }
    if (out.kind === 'gone') {
      setBanner({ tone: 'warn', text: `Nothing changed: that entry isn't shared any more (the reader may have made it private). The list has been reloaded.` });
      await load(page);
      return;
    }
    if (out.kind === 'refused') {
      setResult(row.id, { tone: 'bad', text: `NOT ${past.toLowerCase()}, nothing changed: ${out.message}` });
      return;
    }
    // The change may have gone through, in which case the row is gone after the reload and a note on it would go with it: say it in the banner.
    setBanner({ tone: 'bad', text: `No answer from the database, so I can't tell whether "${row.prompt_label}"${row.username ? ` by @${row.username}` : ''} was ${past.toLowerCase()}. The list has been reloaded: check it. (${out.message})` });
    await load(page);
  }

  const hide = (row) => act(row, 'admin_hide_shared_entry', 'hide', 'Hidden');
  const dismiss = (row) => act(row, 'admin_dismiss_shared_entry', 'dismiss', 'Dismissed');

  const pages = total ? Math.max(1, Math.ceil(total / PAGE)) : 1;

  return (
    <div style={{ padding: isMobile ? 16 : '30px 36px', fontFamily: 'var(--font-body)', maxWidth: 900 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Journal</h1>
        <p data-opening style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 6, lineHeight: 1.55 }}>
          Shared journal entries only. Private entries aren&apos;t readable here, and nothing a reader wrote inside any entry is shown. Each row is the book and the prompt the reader chose, which is what the public card prints. When the reader picked &ldquo;type your own&rdquo;, the prompt is their own words, so that is what to look at.
        </p>
      </div>

      {banner && (
        <div role={banner.tone === 'ok' ? 'status' : 'alert'} data-banner style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: TONE[banner.tone].bg, color: TONE[banner.tone].color, border: `1px solid ${TONE[banner.tone].border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          {banner.text}
        </div>
      )}

      {loading && <p role="status" style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>Loading…</p>}

      {!loading && loadError && (
        <div role="alert" data-load-failed style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          Couldn&apos;t load the shared entries: {loadError}. That is not the same as nothing being shared.{' '}
          <button type="button" onClick={() => load(page)} style={{ fontFamily: 'inherit', cursor: 'pointer' }}>Try again</button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div data-count style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
            {total === 0 ? 'Nothing to look at' : `${entryCountText(total)} to look at`}
          </div>
          {total === 0 && (
            <div data-empty style={{ padding: '32px 0', textAlign: 'center', color: 'var(--neutral-400)', fontStyle: 'italic' }}>
              No shared journal entries to look at. Anything you dismiss stays off this list until the reader changes its label.
            </div>
          )}
          {rows.map(r => (
            <EntryRow key={r.id} row={r} isMobile={isMobile}
              busy={busyId === r.id} confirming={confirmId === r.id} result={results[r.id]}
              onAskHide={row => { setConfirmId(row.id); setResult(row.id, null); }}
              onCancelHide={() => setConfirmId(null)}
              onHide={hide} onDismiss={dismiss} />
          ))}
          {total > PAGE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>Page {page + 1} of {pages}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Newer</Btn>
                <Btn disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Older →</Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
